import React, { ReactNode, useMemo } from "react";
import styled from "@emotion/styled";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { matchPath, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { RangeSelectorControlProps, rangeControlChanged } from "@hooks";
import { Tab, Tabs } from "@rentablo/components";
import { InvestmentsTable } from "@features/investments/investment/components/InvestmentTable";
import { useInvestmentListView } from "@features/investments/investment/components/InvestmentTable/mobile";
import { useListAndSearchAllInvestments } from "@rentablo/api/apiv1";
import { InvestmentActionsBar } from "@features/depots/components/DepotsDashboard/InvestmentActions";
import { MandatorTypography } from "@rentablo/components";
import { InvestmentsChartDataStatistic } from "@api/v1/hooks/performance/useInvestmentsChartData";
import { keepPreviousData } from "@tanstack/react-query";
import { MobileListSettingsButton } from "@components/MobileList";
import { InvestmentsTableSummary } from "./InvestmentsTableSummary/InvestmentsTableSummary";
import { BookingsTableContainer } from "./BookingsTableContainer";
import { useBookingListView } from "./BookingsTableContainer/mobile";
import { ExportButton } from "./ExportButton";
import { MobileTabSwitcher } from "./MobileTabSwitcher";
import { InvestmentListSettings } from "./mobileSettings/InvestmentListSettings";
import { BookingsListSettings } from "./mobileSettings/BookingsListSettings";
import { DashboardTab, useActiveTabParam } from "./useActiveTabParam";
import {
  filterInvestmentsByType,
  useAssetClassFilter,
} from "./assetClassFilter";

const BookingsTableWrapper = styled.div(({ theme }) => ({
  marginTop: theme.spacing(6),
  // Phones open the bookings with the investments tab's summary, in its place.
  [theme.breakpoints.down("sm")]: { marginTop: 0 },
}));

interface DepotsDashboardContentProps {
  accountIds: number[];
  statistic: InvestmentsChartDataStatistic;
  currency?: string;
  summaryCurrency?: string;
  showHistoric: boolean;
  onShowHistoricChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  bookingsRangeProps: RangeSelectorControlProps;
  // Page specific controls for the phone settings menu (e.g. a currency toggle).
  mobileSettingsExtra?: ReactNode;
  // Whether those page specific controls differ from their defaults.
  mobileSettingsChanged?: boolean;
  // Resets the settings the page holds for that tab (e.g. range, currency).
  onMobileSettingsReset?: (tab: DashboardTab) => void;
}

export const DepotsDashboardContent = ({
  accountIds,
  statistic,
  currency,
  summaryCurrency,
  showHistoric,
  onShowHistoricChange,
  bookingsRangeProps,
  mobileSettingsExtra,
  mobileSettingsChanged = false,
  onMobileSettingsReset,
}: DepotsDashboardContentProps) => {
  const location = useLocation();
  const isDepotPage = Boolean(matchPath("/depots/*", location.pathname));
  const isSM = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { t } = useTranslation("");
  const navigate = useNavigate();
  const [activeTabKey, setActiveTab] = useActiveTabParam();
  const investmentListView = useInvestmentListView();
  const bookingListView = useBookingListView();
  const isBookingsTab = activeTabKey === "bookings";
  const {
    isFiltered,
    investmentTypes,
    clear: clearAssetClasses,
  } = useAssetClassFilter();

  const handleInvestmentTableCLick = (url: string) => {
    navigate(
      isDepotPage
        ? `/depots/${accountIds[0]}/investments/${url}`
        : `/investments/${url}`
    );
  };

  const {
    data: investmentsResult,
    isLoading: isInvestmentsLoading,
    isPending: isInvestmentsPending,
    isFetching: isInvestmentsFetching,
  } = useListAndSearchAllInvestments(
    {
      accountId: accountIds,
      includeHistoric: showHistoric,
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  // The list isn't paged, so the asset class filter applies on the client.
  const investments = useMemo(
    () =>
      filterInvestmentsByType(
        investmentsResult?.investments ?? [],
        investmentTypes
      ),
    [investmentsResult, investmentTypes]
  );
  const showFilterEmpty =
    isFiltered && !isInvestmentsFetching && investments.length === 0;

  const exportParams = {
    accountId: accountIds,
    includeHistoric: showHistoric,
    minDate: bookingsRangeProps.range.startDate.toISOString(),
    maxDate: bookingsRangeProps.range.endDate.toISOString(),
  };

  // Exporting acts on the whole list, so on phones it sits in the header line
  // above the list: always in reach, unlike the end of an endless list.
  const exportButton = (tab: "investments" | "bookings") => (
    <ExportButton
      compact
      activeTab={tab}
      currency={currency}
      params={exportParams}
    />
  );

  const mobileSettingsHaveChanges =
    mobileSettingsChanged ||
    isFiltered ||
    (isBookingsTab
      ? rangeControlChanged(bookingsRangeProps) ||
        bookingListView.isSortChanged ||
        bookingListView.types.length > 0
      : showHistoric || investmentListView.isSortChanged);

  const handleMobileSettingsReset = () => {
    clearAssetClasses();
    if (isBookingsTab) bookingListView.reset();
    else investmentListView.reset();
    onMobileSettingsReset?.(activeTabKey);
  };

  const settingsButton = (
    <MobileListSettingsButton
      hasChanges={mobileSettingsHaveChanges}
      canReset={
        mobileSettingsHaveChanges ||
        (!isBookingsTab && investmentListView.isPreferencesChanged)
      }
      onReset={handleMobileSettingsReset}
    >
      {isBookingsTab ? (
        <BookingsListSettings
          view={bookingListView}
          bookingsRangeProps={bookingsRangeProps}
        >
          {mobileSettingsExtra}
        </BookingsListSettings>
      ) : (
        <InvestmentListSettings
          view={investmentListView}
          showHistoric={showHistoric}
          onShowHistoricChange={onShowHistoricChange}
        >
          {mobileSettingsExtra}
        </InvestmentListSettings>
      )}
    </MobileListSettingsButton>
  );

  const investmentsContent = (
    <>
      <InvestmentsTableSummary
        statistic={statistic}
        currency={summaryCurrency ?? currency}
      />
      <InvestmentsTable
        className="investments-table"
        investments={investments}
        routeHandler={handleInvestmentTableCLick}
        isLoading={isInvestmentsLoading}
        isFetching={isInvestmentsFetching}
        isPending={isInvestmentsPending}
        mobileView={investmentListView}
        mobileHeaderAction={exportButton("investments")}
        mobileSettingsAction={settingsButton}
      />
      {showFilterEmpty && (
        <Typography
          variant="body3"
          color="textSecondary"
          textAlign="center"
          py={6}
          data-testid="asset-class-filter-empty"
        >
          {t("assetTypeBreakdown.filter.empty")}
        </Typography>
      )}
    </>
  );

  const bookingsContent = (
    <BookingsTableWrapper>
      {isSM && (
        <InvestmentsTableSummary
          statistic={statistic}
          currency={summaryCurrency ?? currency}
          valueOnly
        />
      )}
      <BookingsTableContainer
        rangeProps={bookingsRangeProps}
        summaryValue={statistic.portfolioValue}
        accountIds={accountIds ?? []}
        displayCurrency={currency}
        summaryCurrency={summaryCurrency}
        mobileHeaderAction={exportButton("bookings")}
        mobileSettingsAction={settingsButton}
        mobileView={bookingListView}
        knownInvestments={investmentsResult?.investments}
      />
    </BookingsTableWrapper>
  );

  return (
    <Box width="100%">
      <MandatorTypography variant="h5" kpmg-germany={{ variant: "h4", mb: -1 }}>
        {t("investmentsPage.overview")}
      </MandatorTypography>

      {isSM ? (
        <Box>
          <Box mb={4}>
            <MobileTabSwitcher
              activeTab={activeTabKey}
              onChange={setActiveTab}
            />
          </Box>
          {isBookingsTab ? bookingsContent : investmentsContent}
        </Box>
      ) : (
        <Tabs
          onChange={setActiveTab}
          activeTabKey={activeTabKey}
          // Both tabs stay mounted: switching neither collapses the page
          // between them nor reloads a tab, which made it flicker.
          keepRendered
          buttonElement={
            <InvestmentActionsBar
              onShowHistoricChange={onShowHistoricChange}
              accountIds={accountIds}
              minDate={bookingsRangeProps.range.startDate.toISOString()}
              maxDate={bookingsRangeProps.range.endDate.toISOString()}
              currency={currency}
              showHistoric={showHistoric}
            />
          }
        >
          <Tab
            label={t("components.depotsStatistic.tabs.investments")}
            key="investments"
          >
            {investmentsContent}
          </Tab>
          <Tab
            label={t("components.depotsStatistic.tabs.bookings")}
            key="bookings"
          >
            {bookingsContent}
          </Tab>
        </Tabs>
      )}
    </Box>
  );
};
